import type { z } from 'zod';
import { parsePath, type PathSegment } from './path';

export interface ZodFieldMeta {
  /** True when the path exists in the schema. */
  found: boolean;
  /** The field rejects `undefined` (no optional / default wrapper). */
  required: boolean;
  nullable: boolean;
  /** Base zod type name: string, number, boolean, enum, date, array, object, literal, union … */
  type: string;
  /** Format for strings (email, uuid, date …) or numbers (safeint …). */
  format?: string;
  enumValues?: Array<string | number>;
  /** Numeric bounds (number) or length bounds (string / array). */
  min?: number;
  max?: number;
  /** Bound inclusivity for numbers. */
  minInclusive?: boolean;
  maxInclusive?: boolean;
  description?: string;
}

type AnyDef = Record<string, unknown> & { type?: string };
type AnySchema = { def?: AnyDef; _zod?: { def?: AnyDef }; safeParse?: (v: unknown) => { success: boolean }; description?: string };

function defOf(schema: unknown): AnyDef {
  const s = schema as AnySchema | null | undefined;
  return (s?.def ?? s?._zod?.def ?? {}) as AnyDef;
}

const WRAPPERS = new Set(['optional', 'nullable', 'default', 'readonly', 'nonoptional', 'catch', 'prefault', 'pipe', 'lazy', 'promise', 'success']);

/** Strips wrapper types, collecting optional / nullable flags along the way. */
function unwrap(schema: unknown): { inner: unknown; optional: boolean; nullable: boolean } {
  let current = schema;
  let optional = false;
  let nullable = false;
  for (let guard = 0; guard < 32; guard += 1) {
    const def = defOf(current);
    const type = def.type;
    if (!type || !WRAPPERS.has(type)) break;
    if (type === 'optional' || type === 'default' || type === 'prefault' || type === 'catch') optional = true;
    if (type === 'nullable') nullable = true;
    if (type === 'pipe') current = def.in;
    else if (type === 'lazy') current = typeof def.getter === 'function' ? (def.getter as () => unknown)() : undefined;
    else current = def.innerType;
    if (current === undefined) break;
  }
  return { inner: current, optional, nullable };
}

function childOf(schema: unknown, segment: PathSegment): unknown {
  const { inner } = unwrap(schema);
  const def = defOf(inner);
  switch (def.type) {
    case 'object': {
      const shape = (typeof def.shape === 'function' ? (def.shape as () => Record<string, unknown>)() : def.shape) as Record<string, unknown> | undefined;
      return shape?.[String(segment)];
    }
    case 'array':
    case 'set':
      return typeof segment === 'number' ? def.element : undefined;
    case 'tuple': {
      const items = def.items as unknown[] | undefined;
      return typeof segment === 'number' ? (items?.[segment] ?? def.rest) : undefined;
    }
    case 'record':
    case 'map':
      return def.valueType;
    case 'union':
    case 'discriminatedUnion':
    case 'intersection': {
      const options = (def.options as unknown[] | undefined) ?? [def.left, def.right].filter(Boolean);
      for (const option of options) {
        const child = childOf(option, segment);
        if (child !== undefined) return child;
      }
      return undefined;
    }
    default:
      return undefined;
  }
}

interface CheckDef {
  check?: string;
  minimum?: number;
  maximum?: number;
  value?: number;
  inclusive?: boolean;
  format?: string;
}

function checksOf(schema: unknown): CheckDef[] {
  const def = defOf(schema);
  const checks = (def.checks as Array<{ _zod?: { def?: CheckDef }; def?: CheckDef }> | undefined) ?? [];
  return checks.map((c) => c._zod?.def ?? c.def ?? {});
}

/**
 * Inspects a zod (v4) schema at `path` and returns presentation metadata (required marker, enum options,
 * numeric / length bounds). Defensive: unknown structures yield `found: false`.
 */
export function zodFieldMeta(schema: z.ZodType, path: string): ZodFieldMeta {
  let node: unknown = schema;
  for (const seg of parsePath(path)) {
    node = childOf(node, seg);
    if (node === undefined) return { found: false, required: false, nullable: false, type: 'unknown' };
  }
  const { inner, optional, nullable } = unwrap(node);
  const def = defOf(inner);
  const type = def.type ?? 'unknown';
  let required = !optional;
  const safeParse = (node as AnySchema).safeParse;
  if (typeof safeParse === 'function') {
    try {
      required = !safeParse.call(node, undefined).success;
    } catch {
      // keep the structural answer
    }
  }
  const meta: ZodFieldMeta = { found: true, required, nullable, type };
  const description = (node as AnySchema).description ?? (inner as AnySchema).description;
  if (description) meta.description = description;

  if (type === 'enum') {
    const entries = def.entries as Record<string, string | number> | undefined;
    if (entries) meta.enumValues = Object.values(entries);
  } else if (type === 'literal') {
    const values = def.values as Array<string | number> | undefined;
    if (values) meta.enumValues = values;
  }
  if (typeof def.format === 'string') meta.format = def.format;
  for (const check of checksOf(inner)) {
    switch (check.check) {
      case 'greater_than':
        meta.min = check.value;
        meta.minInclusive = check.inclusive ?? false;
        break;
      case 'less_than':
        meta.max = check.value;
        meta.maxInclusive = check.inclusive ?? false;
        break;
      case 'min_length':
        meta.min = check.minimum;
        break;
      case 'max_length':
        meta.max = check.maximum;
        break;
      case 'length_equals':
        meta.min = check.minimum ?? check.maximum;
        meta.max = check.maximum ?? check.minimum;
        break;
      case 'number_format':
      case 'string_format':
        if (check.format && !meta.format) meta.format = check.format;
        break;
      default:
        break;
    }
  }
  return meta;
}
